import { useState } from "react";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { BuyNowPayLaterOutlined as BuyNowPayLater48PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/BuyNowPayLaterOutlined";
import styles from "./PaymentPlanOffer.module.scss";
import { PaymentPlansDrawer } from "./PaymentPlansDrawer/PaymentPlansDrawer";
import { PaymentPlansAnalytics } from "../../analytics";
import { CREDIT_OFFER_OPEN_DRAWER } from "../../monetateEvents";
import { CreditIllustration } from "@/types/cobeTypes";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

export type PaymentPlanOfferProps = {
  offers: CreditIllustration[];
  analytics: PaymentPlansAnalytics;
};

const generateOfferLabel = (offer: CreditIllustration) => {
  const {
    financialProductType,
    repaymentAmount: { display: monthlyRepaymentDisplay },
    creditTerm: { display: creditTermDisplay },
    creditRate: { display: creditRateDisplay },
    holidayPeriod,
  } = offer;

  const isIFC = financialProductType === "IFC";

  if (financialProductType === "IFC" || financialProductType === "IBC") {
    return (
      <span>
        Pay {monthlyRepaymentDisplay} per month {isIFC && "interest free"} over{" "}
        {creditTermDisplay}
        {!isIFC && ` at ${creditRateDisplay} APR`}
      </span>
    );
  }

  if (financialProductType === "DEFERRED_IBC") {
    return (
      <span>
        Pay your balance in full within {holidayPeriod?.display} and pay no
        interest
      </span>
    );
  }

  return <></>;
};

export function PaymentPlanOffer({ offers, analytics }: PaymentPlanOfferProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [offer] = offers;
  if (!offer) return null;

  const isDeferred = offer.financialProductType === "DEFERRED_IBC";

  const handleOpenDrawer = () => {
    dispatchMonetateQEvent(CREDIT_OFFER_OPEN_DRAWER);

    if (offer.financialProductType === "IFC") {
      analytics.ifcDisclaimerAnalyticsClickHandler(
        "payment plans credit component",
      );
    } else {
      analytics.ibcDisclaimerAnalyticsClickHandler(
        "payment plans fs slide draw",
        "pdp",
      );
    }

    setIsDrawerOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={styles.offer}
        onClick={handleOpenDrawer}
        data-component="PaymentPlanOffer"
        data-testid="payment-plans-primary-offer"
      >
        <span className={`${styles.icon} ${styles.iconPadded}`}>
          {isDeferred ? (
            <BuyNowPayLater48PxOutlinedIcon size="extra-large" />
          ) : (
            <>
              <span className={styles.rate}>{offer.creditRate.display}</span>
              <span className={styles.rateLabel}>APR</span>
            </>
          )}
        </span>
        <span className={styles.text}>
          <span className={styles.label}>{generateOfferLabel(offer)}</span>
          {offers.length > 1 && (
            <span className={styles.description}>
              More payment plans available.
            </span>
          )}
        </span>
        <ChevronRight size="medium" className={styles.chevron} />
      </button>
      <PaymentPlansDrawer
        handleClose={() => setIsDrawerOpen(false)}
        drawerIsOpen={isDrawerOpen}
        creditOffers={offers}
        ifcDisclaimerAnalyticsClickHandler={
          analytics.ifcDisclaimerAnalyticsClickHandler
        }
        ibcDisclaimerAnalyticsClickHandler={
          analytics.ibcDisclaimerAnalyticsClickHandler
        }
      />
    </>
  );
}
